import { safeDecodeReturnUrl } from '@/lib/return-url';
/**
 * Pre-render deep-link restoration for the post-Ping redirect.
 *
 * `main.tsx`'s `initOidc()` completes the `/auth/callback` code exchange and
 * may surface a `state` string that `AuthedLayout`/`auth-actions.signIn`
 * packed in *before* the Ping round-trip (via `encodeReturnUrl`). `main.tsx`
 * reads the restored `state` off the `User` returned by
 * `getUserManager().getUser()`, called after the callback resolves — not
 * off `signinRedirectCallback()`'s own return value. When present, we
 * rewrite the URL with
 * `history.replaceState` so React Router boots at the deep-link target
 * rather than the consumed `/auth/callback?code=...` URL.
 *
 * The function is intentionally pure-side-effect: it returns `void` and
 * does not throw. `safeDecodeReturnUrl` rejects every open-redirect
 * shape (protocol-relative, encoded slashes, control chars, `javascript:`,
 * absolute URLs); on rejection we leave the URL alone and the consuming
 * surface (`AuthCallback`'s fallthrough Navigate) drops the user at `/`.
 *
 * Using `history.replaceState` — not `useNavigate` — because this runs
 * BEFORE React renders, so router hooks aren't available. React Router
 * reads `window.location` on first render, so the layout boots at
 * `target` once we've rewritten the URL.
 *
 * The `win` parameter is injected for unit-testability — the production
 * caller in `main.tsx` passes the default global `window`.
 */
export function restoreDeepLink(state: unknown, win: Window = window): void {
  if (typeof state !== 'string' || state.length === 0) return;
  const target = safeDecodeReturnUrl(state);
  if (target === undefined) return;
  const current = win.location.pathname + win.location.search + win.location.hash;
  if (target === current) return;
  win.history.replaceState(null, '', target);
}
